import { useMemo, useState } from "react";
import clsx from "clsx";
import {
  Pager,
  SearchInput,
  SegToggle,
} from "../history/HistoryPrimitives";
import {
  AdminButton,
  AdminSection,
  CheckboxRow,
  Chip,
  HourInput,
  Notice,
  TextInput,
} from "./AdminPrimitives";
import {
  useAdminLots,
  useDeleteLotOverride,
  useUpsertLotOverride,
} from "../../hooks/useAdmin";
import { healthStyle } from "../../lib/statusStyles";
import type {
  DownHoursKey,
  LotAdminDto,
  ThresholdsDto,
  UpsertLotOverrideRequest,
} from "../../types/admin";

const PAGE_SIZE = 15;

/** 편집 폼의 4칸 — 라벨과 요청 필드명을 한 곳에 묶어 둔다. */
const HOUR_FIELDS: { key: DownHoursKey; label: string; hint: string }[] = [
  { key: "downAfterHoursLarge", label: "평일 주간 · 대", hint: "면수 기준 이상인 층" },
  { key: "downAfterHoursSmall", label: "평일 주간 · 소", hint: "면수 기준 미만인 층" },
  { key: "downAfterHoursNight", label: "야간", hint: "요일·면수 무관" },
  { key: "downAfterHoursWeekend", label: "주말 주간", hint: "면수 무관" },
];

/** 편집 중인 값 — 서버 요청 본문과 같은 모양이라 그대로 보낼 수 있다. */
type Draft = UpsertLotOverrideRequest;

function draftOf(lot: LotAdminDto): Draft {
  const o = lot.override;
  return {
    downAfterHoursLarge: o?.downAfterHoursLarge ?? null,
    downAfterHoursSmall: o?.downAfterHoursSmall ?? null,
    downAfterHoursNight: o?.downAfterHoursNight ?? null,
    downAfterHoursWeekend: o?.downAfterHoursWeekend ?? null,
    monitored: o?.monitored ?? true,
    note: o?.note ?? null,
  };
}

/** 예외가 하나라도 걸려 있는지 (임계 4칸 중 하나라도 값이 있거나 감시 제외). */
function hasOverride(lot: LotAdminDto): boolean {
  const o = lot.override;
  if (!o) return false;
  return (
    !o.monitored ||
    o.downAfterHoursLarge != null ||
    o.downAfterHoursSmall != null ||
    o.downAfterHoursNight != null ||
    o.downAfterHoursWeekend != null
  );
}

/** 상태 배지 — 조회 화면과 같은 색/라벨을 쓰되 감시 제외는 따로 표기한다. */
function StatusCell({ lot }: { lot: LotAdminDto }) {
  const style = healthStyle(lot.status);
  const label = lot.monitored ? style.label : "감시 제외";
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        style.bg,
        style.fg,
        style.border,
      )}
    >
      <span
        aria-hidden="true"
        className={clsx("inline-block h-1.5 w-1.5 rounded-full", style.solid)}
      />
      {label}
    </span>
  );
}

/** "2 / 3 / 6 / 6" 형태의 적용 중 임계 요약. 예외로 덮인 칸은 굵게. */
function EffectiveCell({ lot }: { lot: LotAdminDto }) {
  const o = lot.override;
  return (
    <span className="tabular-nums text-[11px] text-slate-500 dark:text-slate-400">
      {HOUR_FIELDS.map((f, i) => {
        const overridden = o?.[f.key] != null;
        return (
          <span key={f.key}>
            {i > 0 && <span className="text-slate-300 dark:text-slate-600"> / </span>}
            <span
              className={clsx(
                overridden &&
                  "font-bold text-slate-800 underline decoration-dotted underline-offset-2 dark:text-slate-100",
              )}
            >
              {lot.effective[f.key]}
            </span>
          </span>
        );
      })}
      <span className="ml-1 text-slate-400 dark:text-slate-500">h</span>
    </span>
  );
}

/** 한 주차장의 편집 폼 (행 아래로 펼쳐진다). */
function LotEditor({
  lot,
  globalThresholds,
  onClose,
  token,
}: {
  lot: LotAdminDto;
  globalThresholds: ThresholdsDto;
  onClose: () => void;
  token: string | null;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(lot));
  const upsert = useUpsertLotOverride(token);
  const remove = useDeleteLotOverride(token);

  const pending = upsert.isPending || remove.isPending;
  const error = upsert.error ?? remove.error;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <div className="space-y-4 border-t border-surface-border bg-slate-50/60 px-4 py-4 dark:border-surface-darkBorder dark:bg-slate-900/40">
      <div>
        <div className="mb-2 text-[11px] font-medium text-slate-600 dark:text-slate-300">
          데이터 정체 임계 (시간)
          <span className="ml-1.5 font-normal text-slate-400 dark:text-slate-500">
            비워 두면 전역 기본값을 씁니다
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {HOUR_FIELDS.map((f) => (
            <HourInput
              key={f.key}
              label={f.label}
              hint={f.hint}
              value={draft[f.key]}
              placeholder={`전역 ${globalThresholds[f.key]}`}
              onChange={(v) => set(f.key, v)}
            />
          ))}
        </div>
      </div>

      <CheckboxRow
        checked={!draft.monitored}
        onChange={(v) => set("monitored", !v)}
        label="이 주차장을 감시에서 제외"
        hint="제외하면 상태가 '감시 제외'로 고정되고 종합 상태·Teams 알림·사건일지에서 빠집니다. 이관·폐기 예정 주차장 정리용."
      />

      <TextInput
        label="메모"
        value={draft.note ?? ""}
        onChange={(v) => set("note", v === "" ? null : v)}
        placeholder="예외를 둔 이유 (나중에 왜 이런 값인지 추적할 수 있게)"
      />

      {error && <Notice tone="error">{error.message}</Notice>}

      <div className="flex flex-wrap items-center gap-2">
        <AdminButton
          tone="primary"
          disabled={pending}
          onClick={() =>
            upsert.mutate(
              { siteKey: lot.siteKey, lotId: lot.lotId, body: draft },
              { onSuccess: onClose },
            )
          }
        >
          {upsert.isPending ? "저장 중…" : "저장"}
        </AdminButton>
        <AdminButton disabled={pending} onClick={onClose}>
          취소
        </AdminButton>
        {hasOverride(lot) && (
          <AdminButton
            tone="danger"
            disabled={pending}
            onClick={() =>
              remove.mutate(
                { siteKey: lot.siteKey, lotId: lot.lotId },
                { onSuccess: onClose },
              )
            }
          >
            {remove.isPending ? "삭제 중…" : "예외 삭제"}
          </AdminButton>
        )}
      </div>
    </div>
  );
}

/**
 * 주차장별 임계값 예외 / 감시 제외 관리.
 *
 * 목록은 사이트 DB 폴링 결과(상태 캐시)를 그대로 따른다. 예외 행만 남고 사이트에서
 * 사라진 주차장은 '목록에 없음' 으로 표시해 정리를 유도한다.
 */
export default function LotOverridePanel({
  token,
  globalThresholds,
}: {
  token: string | null;
  globalThresholds: ThresholdsDto;
}) {
  const { data, isLoading, error } = useAdminLots(token);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [onlyOverridden, setOnlyOverridden] = useState<"all" | "override">("all");
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);

  const lots = data?.lots ?? [];

  // 사이트가 하나뿐이거나 목록이 비었으면 필터를 그리지 않는다 —
  // "전체" 버튼만 덩그러니 남아 옆의 예외 필터와 헷갈린다.
  const siteOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const lot of lots) seen.set(lot.siteKey, lot.siteName);
    if (seen.size < 2) return null;
    return [
      { value: "all", label: "전체" },
      ...[...seen].map(([value, label]) => ({ value, label })),
    ];
  }, [lots]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lots.filter((lot) => {
      if (siteFilter !== "all" && lot.siteKey !== siteFilter) return false;
      if (onlyOverridden === "override" && !hasOverride(lot)) return false;
      if (!q) return true;
      return (
        (lot.lotName ?? "").toLowerCase().includes(q) ||
        lot.lotId.toLowerCase().includes(q)
      );
    });
  }, [lots, search, siteFilter, onlyOverridden]);

  // 필터가 바뀌어 페이지가 범위를 벗어나면 첫 페이지로 당긴다.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const overriddenCount = lots.filter(hasOverride).length;

  return (
    <AdminSection
      title="주차장별 임계값"
      description={`전역 기본값과 다르게 판정할 주차장만 예외를 둡니다. 현재 예외 ${overriddenCount}건 / 전체 ${lots.length}곳.`}
    >
      {/* 모바일에서 검색창이 한 줄을 다 쓰도록 — 토글들은 그 아래로 흐른다. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 max-sm:flex-col max-sm:items-stretch">
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(0);
          }}
          placeholder="주차장명 · 코드 검색"
        />
        {siteOptions && (
          <SegToggle
            value={siteFilter}
            onChange={(v) => {
              setSiteFilter(v);
              setPage(0);
            }}
            options={siteOptions}
            ariaLabel="사이트 필터"
          />
        )}
        <SegToggle
          value={onlyOverridden}
          onChange={(v) => {
            setOnlyOverridden(v);
            setPage(0);
          }}
          options={[
            { value: "all", label: "전체 주차장" },
            { value: "override", label: "예외만" },
          ]}
          ariaLabel="예외 여부 필터"
        />
      </div>

      {error && <Notice tone="error">{error.message}</Notice>}

      {isLoading ? (
        <div className="py-10 text-center text-sm text-slate-400 dark:text-slate-500">
          불러오는 중…
        </div>
      ) : lots.length === 0 ? (
        <Notice tone="info">
          아직 폴링된 주차장이 없습니다. 사이트 DB 연결을 확인하거나 아래 '운영 액션'에서
          즉시 폴링을 실행해보세요.
        </Notice>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-slate-400 dark:text-slate-500">
          조건에 맞는 주차장이 없습니다.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-surface-border dark:border-surface-darkBorder">
          <div className="hidden bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 sm:flex sm:items-center dark:bg-slate-800/50 dark:text-slate-500">
            <span className="flex-1">주차장</span>
            <span className="w-28 shrink-0">상태</span>
            <span className="w-44 shrink-0">
              적용 임계 (대/소/야간/주말)
            </span>
            <span className="w-16 shrink-0 text-right">편집</span>
          </div>
          <ul className="divide-y divide-surface-border dark:divide-surface-darkBorder">
            {visible.map((lot) => {
              const key = `${lot.siteKey} ${lot.lotId}`;
              const isEditing = editing === key;
              return (
                <li key={key} className="bg-surface dark:bg-surface-darkSubtle">
                  <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-0">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                          {lot.lotName || lot.lotId}
                        </span>
                        {hasOverride(lot) && <Chip tone="warn">예외</Chip>}
                        {!lot.inCache && <Chip tone="muted">목록에 없음</Chip>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-slate-400 dark:text-slate-500">
                        <span>{lot.siteName}</span>
                        <span className="font-mono">{lot.lotId}</span>
                        {lot.override?.note && (
                          <span className="italic">“{lot.override.note}”</span>
                        )}
                      </div>
                    </div>
                    {/*
                      고정 폭(w-28/w-44/w-16)은 데스크톱 표 정렬용이라 sm: 이상에서만 건다.
                      모바일은 세로 스택이므로 폭을 묶으면 버튼 글자가 잘린다.
                    */}
                    <div className="shrink-0 sm:w-28">
                      <StatusCell lot={lot} />
                    </div>
                    <div className="shrink-0 sm:w-44">
                      {/* 모바일에는 표 헤더가 없어 "2 / 3 / 6 / 6 h" 만으로는 무슨 값인지 알 수 없다. */}
                      <span className="mr-1.5 text-[10px] text-slate-400 sm:hidden dark:text-slate-500">
                        적용 임계 대/소/야간/주말
                      </span>
                      <EffectiveCell lot={lot} />
                    </div>
                    <div className="shrink-0 sm:w-16 sm:text-right">
                      <AdminButton
                        onClick={() => setEditing(isEditing ? null : key)}
                      >
                        {isEditing ? "닫기" : "편집"}
                      </AdminButton>
                    </div>
                  </div>
                  {isEditing && (
                    <LotEditor
                      lot={lot}
                      globalThresholds={globalThresholds}
                      token={token}
                      onClose={() => setEditing(null)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <Pager
        page={safePage}
        size={PAGE_SIZE}
        total={filtered.length}
        hasNext={safePage < pageCount - 1}
        onPage={setPage}
      />
    </AdminSection>
  );
}
